import { useEffect, useRef, useState } from 'react';

// iter87 — Lightweight pull-to-refresh for mobile/PWA. DATA-ONLY: it dispatches a custom
// 'nexflow:refresh' window event that any page can listen for; it never touches the cache,
// service worker, or app shell. Safe to use casually in the field.
//
// Activation rules (all must be true to avoid hijacking normal scrolling):
//   • document.scrollingElement.scrollTop === 0 at touchstart
//   • Touch delta-Y exceeds 70px downward
//   • Touch device only (no mouse)
//
// On release past threshold:
//   • Visual feedback (spinner) is shown for ~600ms
//   • Window dispatches `new CustomEvent('nexflow:refresh')`
//   • If the consumer also passed an `onRefresh` callback (e.g. App-level), call that too.

const ACTIVATION_THRESHOLD_PX = 70;
const MAX_PULL_PX = 140;

const PullToRefresh = () => {
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startYRef = useRef(null);
  const activeRef = useRef(false);

  useEffect(() => {
    // Only activate on touch-capable devices (mobile/tablet).
    const isTouch = typeof window !== 'undefined' && ('ontouchstart' in window || (navigator.maxTouchPoints || 0) > 0);
    if (!isTouch) return undefined;

    const isAtTop = () => {
      const el = document.scrollingElement || document.documentElement;
      return el.scrollTop <= 0;
    };

    const onTouchStart = (e) => {
      if (!isAtTop()) return;
      if (!e.touches || e.touches.length !== 1) return;
      startYRef.current = e.touches[0].clientY;
      activeRef.current = true;
    };

    const onTouchMove = (e) => {
      if (!activeRef.current || startYRef.current == null) return;
      const dy = e.touches[0].clientY - startYRef.current;
      if (dy <= 0) {
        // Upward — cancel the gesture.
        activeRef.current = false;
        startYRef.current = null;
        setPullDistance(0);
        return;
      }
      if (!isAtTop()) {
        activeRef.current = false;
        startYRef.current = null;
        setPullDistance(0);
        return;
      }
      // Damped pull for nicer feel — diminishing returns past threshold.
      const damped = Math.min(MAX_PULL_PX, dy * 0.5);
      setPullDistance(damped);
    };

    const fire = () => {
      setRefreshing(true);
      try { window.dispatchEvent(new CustomEvent('nexflow:refresh')); } catch { /* */ }
      // Auto-clear the spinner after a short cosmetic delay; pages do their own loading state.
      setTimeout(() => {
        setRefreshing(false);
        setPullDistance(0);
      }, 600);
    };

    const onTouchEnd = () => {
      if (!activeRef.current) {
        setPullDistance(0);
        return;
      }
      const dist = pullDistanceRef.current;
      activeRef.current = false;
      startYRef.current = null;
      if (dist >= ACTIVATION_THRESHOLD_PX) {
        fire();
      } else {
        setPullDistance(0);
      }
    };

    // Mirror pullDistance into a ref so onTouchEnd reads the latest without stale closure.
    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: true });
    document.addEventListener('touchend', onTouchEnd, { passive: true });
    document.addEventListener('touchcancel', onTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('touchcancel', onTouchEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep pullDistanceRef in sync with state for use inside the latest onTouchEnd.
  const pullDistanceRef = useRef(0);
  useEffect(() => { pullDistanceRef.current = pullDistance; }, [pullDistance]);

  if (pullDistance <= 0 && !refreshing) return null;

  const active = pullDistance >= ACTIVATION_THRESHOLD_PX || refreshing;
  return (
    <div
      data-testid="pull-to-refresh-indicator"
      className="fixed top-0 left-0 right-0 z-50 pointer-events-none flex justify-center"
      style={{ transform: `translateY(${Math.min(pullDistance, MAX_PULL_PX) - 30}px)`, transition: refreshing ? 'transform 200ms ease-out' : undefined }}
    >
      <div className={`mt-2 px-3 py-1.5 rounded-full bg-[#0A0A0A]/90 border border-[#D4AF37]/40 text-[#D4AF37] text-xs font-medium shadow-lg flex items-center gap-2 ${refreshing ? 'animate-pulse' : ''}`}>
        <svg className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} style={!refreshing ? { transform: `rotate(${active ? 180 : pullDistance * 2.5}deg)`, transition: 'transform 80ms linear' } : undefined} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12a9 9 0 1 1-3-6.7" />
          <path d="M21 4v5h-5" />
        </svg>
        {refreshing ? 'Refreshing…' : (active ? 'Release to refresh' : 'Pull to refresh')}
      </div>
    </div>
  );
};

export default PullToRefresh;
